import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Shipments API
export const shipmentsAPI = {
  create: (data) => api.post('/shipments', data),
  getAll: (params) => api.get('/shipments', { params }),
  getOne: (id) => api.get(`/shipments/${id}`),
  update: (id, data) => api.put(`/shipments/${id}`, data),
  delete: (id) => api.delete(`/shipments/${id}`),
};

// Documents API adaptada para R2
export const documentsAPI = {
  // Upload de documento padrão
  upload: async (shipmentId, docType, file) => {
    // 1️⃣ Solicitar URL presigned para upload
    const formData = new FormData();
    //formData.append('doc_type', docType);
    formData.append('file', file); // usado pelo backend para validar tipo/tamanho

    const res = await api.post(
      `/shipments/${shipmentId}/documents/${docType}/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    // 2️⃣ Fazer PUT direto para o R2
    const { upload_url, filename, r2_key } = res.data;
    const retorno = await fetch(upload_url, {
      method: 'PUT',
      body: file
    });

    // 3️⃣ Retornar no mesmo formato que antes, para compatibilidade
    return {
      message: 'Document uploaded', // igual ao anterior
      filename,                     // mesmo que antes
      r2_key                        // opcional, pode usar no frontend se precisar
    };
  },

  // Upload de documento customizado (mesma lógica)
  createCustom: async (shipmentId, { type, label, required }) => {
    const formData = new FormData();
    formData.append('doc_type', type);
    formData.append('label', label);
    formData.append('required', required);

    const res = await api.post(
      `/shipments/${shipmentId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return res.data;
  },

  // Download usando URL GET assinada
  download: async (shipmentId, docType) => {
    const res = await api.get(`/shipments/${shipmentId}/documents/${docType}`);
    const { download_url } = res.data;
    window.open(download_url, "_blank");
    console.log("Backend response:", res.data);
    return true;
  },
};

// Vendor Link API
export const vendorAPI = {
  generateLink: (shipmentId) =>
    api.post(`/shipments/${shipmentId}/vendor-link`),

  revokeLink: (shipmentId) =>
    api.delete(`/shipments/${shipmentId}/vendor-link`),

  // Public endpoints (no auth)
  getShipment: (token) =>
    axios.get(`${API_BASE}/vendor/${token}`),

  uploadDocument: async (token, docType, file) => {
    // 1️⃣ Pede a presigned URL ao backend
    const formData = new FormData();
    formData.append('doc_type', docType);
    formData.append('file', file); // apenas para pegar ext + content-type

    const { data } = await axios.post(
      `${API_BASE}/vendor/${token}/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    const { upload_url } = data;

    // 2️⃣ Upload direto para o R2
    await axios.put(upload_url, file, {
      headers: {
        'Content-Type': file.type,
      },
    });

    // 3️⃣ Retorno padronizado
    return {
      success: true,
      filename: data.filename,
      r2_key: data.r2_key,
    };
  },
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

export const waitlistAPI = {
  add: async (email, source = 'billing_coming_soon') => {
    const res = await api.post('/waitlist', {
      email,
      source,
    });

    return res.data;
  },
};

// Suppliers API
export const suppliersAPI = {
  create: (data) => api.post('/suppliers', data),
  getAll: () => api.get('/suppliers'),
  getOne: (id) => api.get(`/suppliers/${id}`),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  getShipments: (id) => api.get(`/suppliers/${id}/shipments`),
  getReport: (id) => api.get(`/suppliers/${id}/report`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Admin API (uses separate token storage)
const adminApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export const adminAPI = {
  login: (data) => axios.post(`${API_BASE}/admin/login`, data),
  getUsers: () => adminApi.get('/admin/users'),
  getUser: (id) => adminApi.get(`/admin/users/${id}`),
  updateSubscription: (id, data) => adminApi.put(`/admin/users/${id}/subscription`, data),
  getKPIs: () => adminApi.get('/admin/kpis'),
};

export default api;
